import type { ReactNode } from 'react'
import React from 'react'
import { componentRegistry, type CatalogComponentEntry } from '../lib/component-registry'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { typographyStyle } from '@/shared/lib/catalog'

type CatalogLayer = {
  id: 'shared' | 'entities' | 'features' | 'widgets'
  label: string
  description: string
}

const layers: CatalogLayer[] = [
  {
    id: 'shared',
    label: 'Shared UI',
    description: 'Primitive UI building blocks used across the app.',
  },
  {
    id: 'entities',
    label: 'Entities',
    description: 'Business entity components (plans, payments, subscriptions).',
  },
  {
    id: 'features',
    label: 'Features',
    description: 'Interactive feature components and flows.',
  },
  {
    id: 'widgets',
    label: 'Widgets',
    description: 'Composed UI sections and layout building blocks.',
  },
]

class PreviewErrorBoundary extends React.Component<
  { fallback: ReactNode; children: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: unknown) {
    console.error('Catalog preview failed', error)
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }
    return this.props.children
  }
}

const renderUsage = (name: string, children?: ReactNode) => {
  if (typeof children === 'string' && children.length > 0) {
    return `<${name}>${children}</${name}>`
  }
  return `<${name} />`
}

export function CatalogGalleryTab() {
  const entriesByLayer: Record<CatalogLayer['id'], CatalogComponentEntry[]> = {
    shared: [],
    entities: [],
    features: [],
    widgets: [],
  }
  componentRegistry.forEach((entry) => {
    entriesByLayer[entry.layer].push(entry)
  })

  return (
    <div className="mt-10 space-y-6 px-6">
      <div className="space-y-2">
        <h2 className="text-foreground" style={typographyStyle(designTokens.typography.heading.h2)}>
          Gallery
        </h2>
        <p
          className="max-w-3xl text-muted-foreground"
          style={typographyStyle(designTokens.typography.body.sm)}
        >
          Real components available in the FSD layers. Live previews will be added next.
        </p>
      </div>

      <div className="space-y-10">
        {layers.map((layer) => {
          const entries = entriesByLayer[layer.id] ?? []
          return (
            <section key={layer.id} className="space-y-4">
              <div className="space-y-1">
                <h3
                  className="text-foreground"
                  style={typographyStyle(designTokens.typography.heading.h2)}
                >
                  {layer.label}
                </h3>
                <p
                  className="text-muted-foreground"
                  style={typographyStyle(designTokens.typography.body.sm)}
                >
                  {layer.description}
                </p>
              </div>
              {entries.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
                  No components registered yet.
                </div>
              ) : (
                <div className="grid gap-4 lg:grid-cols-2">
                  {entries.map((entry) => {
                    const Preview = entry.component
                    const usage = renderUsage(entry.name, entry.defaultProps?.children as ReactNode)
                    return (
                      <Card key={`${entry.layer}:${entry.name}`} className="min-w-0">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between gap-4">
                            <CardTitle className="text-sm font-semibold">{entry.name}</CardTitle>
                            <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">
                              {entry.layer}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">{entry.description}</p>
                          <p className="text-xs text-muted-foreground">{entry.path}</p>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4">
                            <PreviewErrorBoundary
                              fallback={
                                <p className="text-sm text-muted-foreground">
                                  Preview failed to render.
                                </p>
                              }
                            >
                              {entry.render ? (
                                entry.render()
                              ) : (
                                <Preview {...(entry.defaultProps ?? {})} />
                              )}
                            </PreviewErrorBoundary>
                          </div>
                          <div className="rounded-md border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
                            <code>{usage}</code>
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              )}
            </section>
          )
        })}
      </div>
    </div>
  )
}
